// Generador ESC/POS puro (sin DOM, sin QZ): convierte las lineas del layout
// compartido en bytes ESC/POS para ticketeras de 80mm (ROCCIA RC-8002 y clones).
import {
  buildTicketLines,
  mergeConfig,
  normalize,
  fmtMonto,
  WIDTH,
} from './ticketLayout';

export { normalize, WIDTH };

const CMD = {
  init: [0x1b, 0x40],
  center: [0x1b, 0x61, 0x01],
  left: [0x1b, 0x61, 0x00],
  boldOn: [0x1b, 0x45, 0x01],
  boldOff: [0x1b, 0x45, 0x00],
  doubleOn: [0x1b, 0x21, 0x30], // doble alto + ancho
  normalSize: [0x1b, 0x21, 0x00],
  // Tamano intermedio (ESC ! n): 0x11 = doble alto, 0x10 = doble ancho.
  tall: [0x1b, 0x21, 0x01],
  wide: [0x1b, 0x21, 0x10],
  cut: [0x1d, 0x56, 0x42, 0x00], // corte parcial con avance
};

function strBytes(s) {
  return normalize(s)
    .split('')
    .map((c) => c.charCodeAt(0) & 0xff);
}

class Ticket {
  constructor() {
    this.bytes = [];
  }

  push(...arr) {
    this.bytes.push(...arr);
  }

  text(s) {
    this.push(...strBytes(s));
  }

  emit(line, globalBold, sizeCmd) {
    const { text, style } = line;
    switch (style) {
      case 'center':
        this.push(...CMD.center);
        this.push(...strBytes(text));
        this.push(0x0a);
        this.push(...CMD.left);
        break;
      case 'bold':
        if (!globalBold) this.push(...CMD.boldOn);
        this.push(...strBytes(text));
        this.push(0x0a);
        if (!globalBold) this.push(...CMD.boldOff);
        break;
      case 'bold-center':
        if (!globalBold) this.push(...CMD.boldOn);
        this.push(...CMD.center);
        this.push(...strBytes(text));
        this.push(0x0a);
        if (!globalBold) this.push(...CMD.boldOff);
        this.push(...CMD.left);
        break;
      case 'double':
        this.push(...CMD.doubleOn, ...CMD.center);
        this.push(...strBytes(text));
        this.push(0x0a);
        this.push(...CMD.normalSize);
        if (sizeCmd) this.push(...sizeCmd);
        this.push(...CMD.left);
        break;
      case 'normal':
      default:
        this.push(...strBytes(text));
        this.push(0x0a);
        break;
    }
  }

  feed(n = 1) {
    for (let i = 0; i < n; i++) this.push(0x0a);
  }

  toUint8Array() {
    return new Uint8Array(this.bytes);
  }
}

// Mapea tamano (px) a un comando ESC/POS de tamano aproximado.
function sizeCommand(tamano) {
  if (!tamano || tamano <= 11) return null;
  if (tamano >= 18) return CMD.doubleOn; // doble alto+ancho
  if (tamano >= 15) return CMD.tall; // doble alto
  if (tamano >= 13) return CMD.wide; // doble ancho
  return null;
}

// Construye el ticket completo a partir del recibo del backend.
export function buildEscPos(recibo, modo, monedasSel = [], incluirIva) {
  const cfg = mergeConfig(recibo?.ticket_config);
  const lines = buildTicketLines(recibo, modo, monedasSel, incluirIva);
  const globalBold = !!cfg.negrita;
  const sizeCmd = sizeCommand(cfg.tamano);

  const t = new Ticket();
  t.push(...CMD.init);
  if (globalBold) t.push(...CMD.boldOn);
  if (sizeCmd) t.push(...sizeCmd);
  lines.forEach((ln) => t.emit(ln, globalBold, sizeCmd));
  if (sizeCmd) t.push(...CMD.normalSize);
  if (globalBold) t.push(...CMD.boldOff);
  t.feed(3);
  t.push(...CMD.cut);
  return t.toUint8Array();
}

// ── Etiqueta de artículo (identificación / precio) ──

// Genera los bytes del código de barras CODE128 (GS k 73) para una ticketera.
export function barcodeBytes(code) {
  const data = normalize(code);
  if (!data) return [];
  const out = [0x1d, 0x6b, 0x49, data.length]; // GS k 73 n (CODE128, selección automática)
  for (let i = 0; i < data.length; i++) out.push(data.charCodeAt(i) & 0xff);
  return out;
}

// Etiqueta ESC/POS de artículo: nombre (negrita), precio opcional (doble tamaño)
// y código de barras CODE128 con el código del artículo.
export function buildEtiquetaEscPos({ nombre, codigo, precio, monedaSimbolo }) {
  const t = new Ticket();
  t.push(...CMD.init);
  t.push(...CMD.center);
  t.push(...CMD.boldOn, ...strBytes(normalize(nombre)), 0x0a, ...CMD.boldOff);
  if (precio != null && !isNaN(parseFloat(precio))) {
    const sim = monedaSimbolo || '$';
    t.push(...CMD.doubleOn, ...strBytes(fmtMonto(precio, sim)), 0x0a, ...CMD.normalSize);
  }
  const bar = barcodeBytes(codigo);
  if (bar.length) {
    t.push(...bar);
    t.push(0x0a);
  }
  t.feed(2);
  t.push(...CMD.cut);
  return t.toUint8Array();
}
